import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Factory, Settings, Cpu } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function ManufacturingPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Manufacturing',
    description: 'Securing Industry 4.0 operations from cyber threats that can halt production',
    url: 'https://thecisonetwork.com/sectors/manufacturing',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Manufacturing',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Manufacturing"
        subtitle="Securing Industry 4.0 operations from cyber threats that can halt production"
        eyebrow="Manufacturing"
        backgroundImage={getSectorCard('manufacturing')?.image}
        backgroundAlt={getSectorCard('manufacturing')?.imageAlt}
        seoTitle="Fractional CISO Manufacturing | The CISO Network"
        seoDescription="Expert fractional CISO services for manufacturing. Protect OT/ICS systems, secure supply chains, and prevent costly production downtime from cyber attacks."
      />

      {/* Why Manufacturing Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Manufacturing
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Manufacturing has undergone a digital revolution. Industry 4.0 technologies -
                  connected machinery, automated production lines, robotics, and IoT sensors - have
                  transformed factory floors into sophisticated cyber-physical systems. While this
                  drives efficiency, it also creates unprecedented cyber risk where digital attacks
                  can cause physical damage.
                </p>
                <p>
                  The UK's manufacturing sector faces a perfect storm of threats. Ransomware gangs
                  have discovered that halting production lines is highly profitable - downtime can
                  cost £50,000+ per hour. State-sponsored actors target manufacturers for
                  intellectual property theft and{' '}
                  <Link to="/services/risk-assessment" className="text-secondary hover:underline">
                    supply chain disruption
                  </Link>
                  . And the convergence of IT and operational technology (OT) creates new attack
                  vectors that traditional IT security cannot address.
                </p>
                <p>
                  Yet many manufacturers lack security leadership. Smaller firms often have no
                  dedicated security function at all. Even larger manufacturers may have IT security
                  teams without the specialised knowledge to protect industrial control systems
                  (ICS). The sector needs CISOs who understand both enterprise IT security and the
                  unique challenges of manufacturing environments.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Factory className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      OT/ICS Expertise
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Our CISOs understand industrial control systems, SCADA networks, and the
                      unique security challenges of operational technology environments.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Settings className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Production-Focused Security
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Security that protects without disrupting production. We understand that
                      uptime is paramount and security must enable, not hinder, manufacturing
                      operations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Manufacturing Under Attack
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real incidents where manufacturers suffered breaches that a CISO could have prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Norsk Hydro (2019)',
                impact: '$71M losses, 35,000 employees affected',
                description:
                  'The aluminium giant was hit by LockerGoga ransomware that encrypted 22,000 computers and forced manual operations. A CISO would have implemented network segmentation between IT and OT, robust backup systems, and incident response capabilities to contain the attack.',
              },
              {
                icon: AlertTriangle,
                title: 'JBS Foods (2021)',
                impact: '$11M ransom paid, UK plants halted',
                description:
                  "The world's largest meat processor had production halted across multiple countries including the UK. A CISO would have established security monitoring, privileged access management, and segmentation to prevent lateral movement by attackers.",
              },
              {
                icon: AlertTriangle,
                title: 'Colonial Pipeline (2021)',
                impact: '$4.4M ransom, fuel shortages',
                description:
                  'While US-based, this demonstrated how critical infrastructure attacks affect supply chains. A CISO would have ensured proper VPN security, multi-factor authentication, and OT network isolation to prevent IT compromises affecting operational systems.',
              },
              {
                icon: AlertTriangle,
                title: 'Eurofins Scientific (2019)',
                impact: 'Police forensic work delayed for weeks',
                description:
                  'The forensic services firm suffered a ransomware attack that affected police laboratories across the UK. A CISO would have implemented comprehensive backup strategies and disaster recovery planning to minimise operational disruption. Our cyber resilience services help manufacturing firms build exactly these capabilities.',
              },
              {
                icon: AlertTriangle,
                title: 'Honda (2020)',
                impact: 'Global production halted',
                description:
                  'The Snake ransomware attack forced Honda to halt production at plants worldwide. A CISO would have established endpoint detection and response (EDR), network monitoring, and incident response procedures to detect and stop attacks before they spread.',
              },
              {
                icon: AlertTriangle,
                title: 'Iranian Steel Mills Attack (2022)',
                impact: 'Production lines physically damaged',
                description:
                  'Hackers caused physical damage to steel production equipment by manipulating industrial control systems. A CISO with OT expertise would implement safety systems, network segmentation, and anomaly detection to prevent cyber-physical attacks.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks in Manufacturing
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats that can halt production and damage competitive advantage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Ransomware',
                description:
                  'Manufacturers pay ransoms quickly because production downtime costs far more than the ransom demand.',
              },
              {
                icon: Cpu,
                title: 'OT/IT Convergence',
                description:
                  'Connecting factory floors to enterprise networks creates pathways for attackers to reach critical control systems.',
              },
              {
                icon: Settings,
                title: 'Intellectual Property Theft',
                description:
                  'Designs, formulas, and manufacturing processes are valuable targets for competitors and nation-states.',
              },
              {
                icon: Factory,
                title: 'Supply Chain Disruption',
                description:
                  'Just-in-time manufacturing means any supplier compromise can halt your entire production line.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Manufacturing - FAQs"
        subtitle="Common questions from manufacturers and industrial businesses"
        items={[
          {
            id: 'manufacturing-1',
            question: 'How does a fractional CISO work with manufacturers?',
            answer:
              'Our CISOs work with manufacturers across the UK, from precision engineering to large-scale production. They understand the unique requirements of OT/IT environments and provide governance that protects production lines while meeting customer security requirements.',
          },
          {
            id: 'manufacturing-2',
            question: 'What cybersecurity risks do manufacturers face?',
            answer:
              'Manufacturers face ransomware that shuts down production lines, supply chain attacks that compromise components, intellectual property theft of designs and processes, and OT breaches that affect industrial control systems. The convergence of IT and operational technology has expanded the attack surface significantly.',
          },
          {
            id: 'manufacturing-3',
            question: 'How does a fractional CISO protect industrial control systems?',
            answer:
              'Our CISOs understand the unique requirements of manufacturing environments where availability is paramount. We implement network segmentation between IT and OT, secure remote access for maintenance, and monitoring that detects threats without disrupting production schedules.',
          },
          {
            id: 'manufacturing-4',
            question: 'Can a fractional CISO help with customer security requirements?',
            answer:
              'Yes. Major manufacturing customers increasingly require suppliers to demonstrate cybersecurity maturity. Our CISOs help you complete customer security questionnaires, achieve relevant certifications like ISO 27001, and implement the controls that protect your position in the supply chain.',
          },
          {
            id: 'manufacturing-5',
            question: 'What is the first thing a fractional CISO does for a manufacturer?',
            answer:
              'Assess the boundary between IT and operational technology. Most manufacturers have unknowingly created pathways between their office networks and production systems. Our CISOs map these connections, identify vulnerabilities, and implement segmentation that protects production without disrupting efficiency.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Secure Your Manufacturing Operations
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Cyber threats to manufacturing are growing, but you don't need a full-time CISO to
            defend against them. Our fractional CISOs bring manufacturing and OT security expertise.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
